import { nowIso } from '@ibe/utils';
import {
  type ApiErrorBody,
  type ApiErrorResponse,
  type ApiListResponse,
  type ApiSuccessResponse,
  type PaginationMeta,
} from '@ibe/types';

import { ApiError, NetworkError } from './errors.js';

/**
 * The Admin Portal's one way of talking to the platform.
 *
 * Everything goes through the Gateway, so there is a single base URL, a single
 * place JWTs are attached and a single place a 401 is handled.
 *
 * **The access token is held in memory and never in `localStorage`.** This
 * portal renders templates written by property administrators, which makes
 * stored XSS a live risk rather than a theoretical one — and a token in
 * `localStorage` turns any XSS into a full account takeover that outlives the
 * tab. The refresh token is an httpOnly cookie the browser sends and no script
 * can read; on reload the app exchanges it for a fresh access token. "Remember
 * me" is therefore a property of that cookie's lifetime, decided by the
 * server, not a flag the client stores.
 */

const UNAUTHORIZED = 401;
const NO_CONTENT = 204;

export { ApiError, NetworkError } from './errors.js';

export interface ApiClientOptions {
  readonly baseUrl: string;
  /** Injected so tests need no network. */
  readonly fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly form?: FormData;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly signal?: AbortSignal;
  /**
   * An idempotency key for a mutation.
   *
   * The Gateway retains these for 24 hours, so a retried booking or payment
   * does not become two. Only meaningful on non-GET requests.
   */
  readonly idempotencyKey?: string;
  /** Return the whole envelope rather than its `data` field. Used by `list`. */
  readonly raw?: boolean;
}

export interface ListResult<TItem> {
  readonly items: readonly TItem[];
  readonly pagination: PaginationMeta;
}

function buildUrl(baseUrl: string, path: string, query: RequestOptions['query']): string {
  const url = new URL(path.replace(/^\//, ''), `${baseUrl.replace(/\/$/, '')}/`);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) continue;
    url.searchParams.set(key, String(value));
  }

  return url.toString();
}

/**
 * Extracts the platform's error envelope.
 *
 * The body is `{ success: false, error: {...} }`, not a bare error object.
 * Reading the top level would silently miss every field — the code, the
 * correlation id, the per-field messages — and every failed save would show
 * the same generic message with nothing to trace it by.
 */
function errorBodyOf(value: unknown): ApiErrorBody | null {
  if (typeof value !== 'object' || value === null) return null;

  const candidate: unknown = (value as Partial<ApiErrorResponse>).error;
  if (typeof candidate !== 'object' || candidate === null) return null;

  const body = candidate as Partial<ApiErrorBody>;
  if (typeof body.code !== 'string' || typeof body.message !== 'string') return null;

  return candidate as ApiErrorBody;
}

export class ApiClient {
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;
  readonly #expiryListeners = new Set<() => void>();

  #accessToken: string | null = null;

  /**
   * The refresh currently in flight, if any.
   *
   * This is the reason the client is a class rather than a function. When a
   * page mounts and fires six queries at once, all six get a 401 the moment
   * the token expires — and six concurrent refreshes present the same
   * single-use refresh token six times. The auth service treats a reused
   * refresh token as theft and revokes the entire token family, which logs
   * the user out of every session they have. Sharing one promise turns six
   * refreshes into one.
   */
  #refreshInFlight: Promise<boolean> | null = null;

  constructor(options: ApiClientOptions) {
    this.#baseUrl = options.baseUrl;
    this.#fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    const stored = sessionStorage.getItem('ibe_access_token');
    if (stored !== null) {
      this.#accessToken = stored;
    }
  }

  /**
   * Subscribes to the session ending, returning an unsubscribe.
   *
   * A subscription rather than a constructor callback because the client is
   * created once, at module scope, and the thing that needs to react to an
   * expiry is a React provider that mounts later and may remount. A callback
   * captured at construction would be pointing at a provider that has been
   * unmounted, and the second sign-in of a session would update nothing.
   */
  onSessionExpired(listener: () => void): () => void {
    this.#expiryListeners.add(listener);
    return () => {
      this.#expiryListeners.delete(listener);
    };
  }

  #notifyExpired(): void {
    for (const listener of this.#expiryListeners) listener();
  }

  get accessToken(): string | null {
    return this.#accessToken;
  }

  setAccessToken(token: string | null): void {
    this.#accessToken = token;
    if (token !== null) {
      sessionStorage.setItem('ibe_access_token', token);
    } else {
      sessionStorage.removeItem('ibe_access_token');
    }
  }

  /**
   * Sends a request, refreshing once and retrying if the token has expired.
   *
   * `TResult` is what the caller gets back: the `data` field by default, or
   * the whole envelope when `raw` is set — which is what a list endpoint needs,
   * since its pagination sits beside `data` rather than inside it.
   */
  async request<TResult>(path: string, options: RequestOptions = {}): Promise<TResult> {
    const response = await this.#send(path, options);

    if (response.status !== UNAUTHORIZED) {
      return await this.#parse(response, options.raw ?? false);
    }

    // One retry, and only one. A second 401 after a successful refresh means
    // the caller lacks the permission rather than the token — retrying that
    // forever is how a client spins against the Gateway's rate limiter.
    const refreshed = await this.#refresh();
    if (!refreshed) {
      this.#accessToken = null;
      this.#notifyExpired();
      return await this.#parse(response, options.raw ?? false);
    }

    return await this.#parse(await this.#send(path, options), options.raw ?? false);
  }

  async get<TData>(path: string, query?: RequestOptions['query']): Promise<TData> {
    return await this.request<TData>(path, { method: 'GET', ...(query ? { query } : {}) });
  }

  /**
   * A list endpoint.
   *
   * Separate from `get` because a list response carries `pagination` alongside
   * `data`, and unwrapping to `data` alone throws the pagination away. A page
   * that has lost it can only render a table with no idea whether there is a
   * second page.
   */
  async list<TItem>(path: string, query?: RequestOptions['query']): Promise<ListResult<TItem>> {
    const envelope = await this.request<ApiListResponse<TItem>>(path, {
      method: 'GET',
      raw: true,
      ...(query ? { query } : {}),
    });

    return { items: envelope.data, pagination: envelope.pagination };
  }

  async post<TData>(path: string, body?: unknown, idempotencyKey?: string): Promise<TData> {
    return await this.request<TData>(path, {
      method: 'POST',
      body,
      ...(idempotencyKey === undefined ? {} : { idempotencyKey }),
    });
  }

  async postForm<TData>(path: string, form: FormData): Promise<TData> {
    return await this.request<TData>(path, { method: 'POST', form });
  }

  async put<TData>(path: string, body?: unknown): Promise<TData> {
    return await this.request<TData>(path, { method: 'PUT', body });
  }

  async patch<TData>(path: string, body?: unknown): Promise<TData> {
    return await this.request<TData>(path, { method: 'PATCH', body });
  }

  async delete<TData>(path: string): Promise<TData> {
    return await this.request<TData>(path, { method: 'DELETE' });
  }

  /**
   * Exchanges the refresh cookie for a new access token.
   *
   * Deduplicated: concurrent callers await the same attempt.
   */
  async #refresh(): Promise<boolean> {
    this.#refreshInFlight ??= this.#performRefresh().finally(() => {
      this.#refreshInFlight = null;
    });

    return await this.#refreshInFlight;
  }

  async #performRefresh(): Promise<boolean> {
    try {
      const response = await this.#fetch(buildUrl(this.#baseUrl, '/v1/auth/refresh', undefined), {
        method: 'POST',
        // The refresh token is an httpOnly cookie; nothing is sent in the body.
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return false;

      const body: unknown = await response.json();
      const token = (body as { data?: { accessToken?: unknown } }).data?.accessToken;
      if (typeof token !== 'string') return false;

      this.#accessToken = token;
      return true;
    } catch {
      // A refresh that could not be attempted is not a session that has
      // ended. Reporting it as one logs a user out because their wifi
      // dropped for a second.
      return false;
    }
  }

  async #send(path: string, options: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };

    if (this.#accessToken !== null) {
      headers.Authorization = `Bearer ${this.#accessToken}`;
    }
    if (options.body !== undefined && options.form === undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (options.idempotencyKey !== undefined) {
      headers['Idempotency-Key'] = options.idempotencyKey;
    }

    try {
      return await this.#fetch(buildUrl(this.#baseUrl, path, options.query), {
        method: options.method ?? 'GET',
        headers,
        credentials: 'include',
        ...(options.form === undefined
          ? options.body === undefined
            ? {}
            : { body: JSON.stringify(options.body) }
          : { body: options.form }),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });
    } catch (error) {
      // An aborted request is the caller changing their mind — a navigation,
      // a new search term — not a failure to report.
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new NetworkError(error);
    }
  }

  async #parse<TResult>(response: Response, raw: boolean): Promise<TResult> {
    if (response.status === NO_CONTENT) return undefined as TResult;

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      // The server answered; the answer was not JSON. That is not a network
      // failure, and reporting it as one tells the user to check a connection
      // that is working while a gateway is down. A successful response with an
      // empty body is simply a response with no data.
      if (response.ok) return undefined as TResult;
      body = undefined;
    }

    if (!response.ok) {
      const errorBody = errorBodyOf(body);
      if (errorBody !== null) throw new ApiError(response.status, errorBody);

      // A non-JSON error, or one that is not the platform's envelope, means
      // something between us and the service answered — a proxy, a load
      // balancer. Its body is not safe to show a user.
      throw new ApiError(response.status, {
        code: 'UNEXPECTED_RESPONSE',
        message: 'The server returned an unexpected response.',
        correlationId: '',
        requestId: '',
        timestamp: nowIso(),
      });
    }

    return raw ? (body as TResult) : (body as ApiSuccessResponse<TResult>).data;
  }
}
